import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { ApiError, api } from '@/lib/api';
import Icon from '@/components/Icon';

export default function LoginPage() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from?.pathname || '/';

  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [forgotOpen, setForgotOpen] = useState(false);
  // Домен потребовал сменить пароль (галочка «при первом входе» или
  // истечение по политике): { reason: 'must_change' | 'expired' }.
  const [adChange, setAdChange] = useState(null);

  // Маршрутизация по роли: родитель идёт в свой ЛК, остальные — в
  // админку (или туда, откуда пришли). Если location.state.from
  // указывает в чужую зону (admin кликнул ссылку /parent/... до
  // логина) — это поправят guard'ы на самих маршрутах.
  const routeAfterLogin = (u) => {
    const isParent = u && u.role === 'parent';
    const target = isParent
      ? (location.state?.from?.pathname?.startsWith('/parent') ? location.state.from.pathname : '/parent')
      : (redirectTo === '/' ? '/' : redirectTo);
    navigate(target, { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      setError('Введите email или AD-логин и пароль');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const u = await login(identifier.trim(), password, remember);
      routeAfterLogin(u);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'ad_password_change_required') {
        // Пароль верный, но AD требует его сменить — открываем диалог.
        setAdChange({ reason: err.payload?.error?.reason || 'must_change' });
        return;
      }
      const msg = err instanceof ApiError ? err.message : 'Не удалось выполнить вход';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo">
          {/* Полноценный логотип школы вместо буквенной заглушки + подписи.
              Файл лежит в public/img — Vite раздаёт его по корню. */}
          <img src="/img/np-logo.png" alt="Новое Поколение" className="login-logo__img" />
        </div>

        <h1 className="login-title">Вход в систему</h1>

        <form className="login-form" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="login-error-banner" role="alert">
              <Icon name="alert" size={14} />
              <span>{error}</span>
            </div>
          )}

          <div className="login-field">
            <label className="login-field__label" htmlFor="login-id">Email или логин</label>
            <input
              id="login-id"
              className={`login-field__input ${error ? 'login-field__input--error' : ''}`}
              type="text"
              autoComplete="username"
              autoFocus
              value={identifier}
              onChange={(e) => { setIdentifier(e.target.value); if (error) setError(null); }}
              placeholder="ivanov или ivanov@np-school.ru"
            />
          </div>

          <div className="login-field">
            <label className="login-field__label" htmlFor="login-pwd">Пароль</label>
            <div className="login-field__password">
              <input
                id="login-pwd"
                className={`login-field__input ${error ? 'login-field__input--error' : ''}`}
                type={showPwd ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
                placeholder="Введите пароль"
              />
              <button
                type="button"
                className="login-field__toggle"
                onClick={() => setShowPwd((s) => !s)}
                aria-label={showPwd ? 'Скрыть пароль' : 'Показать пароль'}
                tabIndex={-1}
              >
                <Icon name={showPwd ? 'eye-off' : 'eye'} size={16} />
              </button>
            </div>
          </div>

          <div className="login-row">
            <label className="login-remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>Запомнить меня</span>
            </label>
            <button type="button" className="login-forgot" onClick={() => setForgotOpen(true)}>
              Забыли пароль?
            </button>
          </div>

          <button type="submit" className="login-submit" disabled={submitting}>
            {submitting ? (
              <>
                <span className="login-submit__spinner" />
                <span>Входим…</span>
              </>
            ) : (
              'Войти'
            )}
          </button>
        </form>

        <div className="login-footer">Учётные записи создаёт администратор школы</div>
      </div>

      <OrgFooter />

      {forgotOpen && <ForgotPasswordModal onClose={() => setForgotOpen(false)} />}

      {adChange && (
        <AdChangePasswordModal
          identifier={identifier.trim()}
          oldPassword={password}
          reason={adChange.reason}
          onClose={() => setAdChange(null)}
          onLoggedIn={async (u) => {
            // Сессия уже установлена бэком — подтягиваем user/права/CSRF.
            await refresh();
            routeAfterLogin(u);
          }}
          onChangedNotLogged={(msg) => {
            setAdChange(null);
            setPassword('');
            setError(msg || 'Пароль изменён. Войдите с новым паролем.');
          }}
        />
      )}
    </div>
  );
}

/**
 * Диалог «домен требует сменить пароль»: показывается, когда login()
 * ответил ad_password_change_required (учётка заведена с галочкой
 * «сменить пароль при первом входе» или пароль истёк по политике).
 * Старый пароль не спрашиваем повторно — он только что был введён в форме
 * и подтверждён доменом (data 773/532 возвращается лишь при верном пароле).
 */
function AdChangePasswordModal({ identifier, oldPassword, reason, onClose, onLoggedIn, onChangedNotLogged }) {
  const [pwd1, setPwd1] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const submit = async (e) => {
    e.preventDefault();
    if (!pwd1) { setErr('Введите новый пароль'); return; }
    if (pwd1 !== pwd2) { setErr('Пароли не совпадают'); return; }
    if (pwd1 === oldPassword) { setErr('Новый пароль должен отличаться от текущего'); return; }
    setErr(null);
    setBusy(true);
    try {
      const res = await api.post('/api/auth/ad-change-password', {
        identifier, oldPassword, newPassword: pwd1,
      });
      if (res.loggedIn) onLoggedIn(res.user);
      else onChangedNotLogged(res.message);
    } catch (e2) {
      setErr(e2 instanceof ApiError ? e2.message : 'Не удалось сменить пароль');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="lf-overlay" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="lf-modal" role="dialog" aria-modal="true">
        <div className="lf-header">
          <div>
            <h2 className="lf-header__title">Смена пароля</h2>
            <p className="lf-header__sub">
              {reason === 'expired'
                ? 'Срок действия вашего доменного пароля истёк. Задайте новый — он сразу подействует и для входа в CRM, и на компьютерах школы.'
                : 'Учётная запись создана с требованием сменить пароль при первом входе. Задайте свой пароль — он сразу подействует и для входа в CRM, и на компьютерах школы.'}
            </p>
          </div>
          <button className="lf-close" onClick={onClose} aria-label="Закрыть" disabled={busy}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <form onSubmit={submit}>
          <div className="lf-body">
            {err && (
              <div className="login-error-banner" role="alert" style={{ marginBottom: 12 }}>
                <Icon name="alert" size={14} />
                <span>{err}</span>
              </div>
            )}

            <div className="login-field">
              <label className="login-field__label" htmlFor="adc-pwd1">Новый пароль</label>
              <div className="login-field__password">
                <input
                  id="adc-pwd1"
                  className="login-field__input"
                  type={show ? 'text' : 'password'}
                  autoComplete="new-password"
                  autoFocus
                  value={pwd1}
                  onChange={(e) => { setPwd1(e.target.value); if (err) setErr(null); }}
                />
                <button type="button" className="login-field__toggle" tabIndex={-1}
                        onClick={() => setShow((s) => !s)}
                        aria-label={show ? 'Скрыть пароль' : 'Показать пароль'}>
                  <Icon name={show ? 'eye-off' : 'eye'} size={16} />
                </button>
              </div>
            </div>

            <div className="login-field" style={{ marginTop: 10 }}>
              <label className="login-field__label" htmlFor="adc-pwd2">Новый пароль ещё раз</label>
              <input
                id="adc-pwd2"
                className="login-field__input"
                type={show ? 'text' : 'password'}
                autoComplete="new-password"
                value={pwd2}
                onChange={(e) => { setPwd2(e.target.value); if (err) setErr(null); }}
              />
            </div>

            <p style={{ fontSize: 12, color: 'var(--fg-muted, #6b7280)', marginTop: 10, marginBottom: 0 }}>
              Требования к паролю задаёт домен: обычно не короче 8 символов, буквы разных
              регистров и цифры; недавно использованные пароли повторять нельзя.
            </p>
          </div>
          <div className="lf-footer">
            <button type="button" className="lf-btn" onClick={onClose} disabled={busy}>Отмена</button>
            <button type="submit" className="lf-btn lf-btn--primary" disabled={busy}>
              {busy ? 'Меняем…' : 'Сменить пароль и войти'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Футер на странице входа: реквизиты школы + соцсети.
 * Иконки SVG inline — те же иконки и ссылки, что в публичном сайте.
 * Стилизуется через .login-orgfoot* (см. login.css) — мелкий, серый,
 * не перетягивает внимание с формы.
 */
function OrgFooter() {
  return (
    <footer className="login-orgfoot">
      <div className="login-orgfoot__legal">
        © АНО ЧОШ «НОВОЕ ПОКОЛЕНИЕ» · ИНН 5032339370 · КПП 503201001 · ОГРН 1225000030243<br />
        <a
          href="https://islod.obrnadzor.gov.ru/view/186112"
          target="_blank"
          rel="noopener noreferrer"
          className="login-orgfoot__link"
        >
          Лицензия № ЛО35‑01255‑50/00891601 от 23.11.2023
        </a><br />
        <a
          href="https://isga.obrnadzor.gov.ru/accredreestr/details/57ee0edf-b1cb-b07e-3124-d2e4b815b58e"
          target="_blank"
          rel="noopener noreferrer"
          className="login-orgfoot__link"
        >
          Государственная аккредитация № А007‑01255‑50/01309224 от 23.07.2024
        </a>
      </div>

      {/* Все SVG приведены к viewBox=24x24 и стилю «filled glyph без подложки»,
          чтобы оптически совпадали по размеру и стояли в одну линию.
          Размер svg задаётся через CSS, а не атрибутами — единое управление. */}
      <ul className="login-orgfoot__social">
        <li>
          <a href="https://vk.com/public218874451" target="_blank" rel="noopener noreferrer" aria-label="ВКонтакте">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12.785 16.241s.288-.032.435-.193c.136-.148.131-.426.131-.426s-.018-1.305.587-1.499c.596-.19 1.361 1.262 2.172 1.821.614.422 1.08.33 1.08.33l2.171-.031s1.135-.073.597-.985c-.044-.075-.314-.679-1.617-1.928-1.366-1.31-1.183-1.097.461-3.34.999-1.367 1.4-2.198 1.273-2.557-.121-.342-.864-.252-.864-.252l-2.471.015s-.183-.026-.318.057c-.133.082-.218.272-.218.272s-.388 1.066-.905 1.972c-1.092 1.913-1.529 2.014-1.708 1.895-.415-.277-.311-1.103-.311-1.69 0-1.832.272-2.594-.524-2.79-.265-.065-.46-.108-1.135-.115-.866-.009-1.598.002-2.013.21-.276.139-.49.448-.36.466.16.022.524.103.717.371.249.347.241 1.122.241 1.122s.143 2.13-.333 2.396c-.327.182-.775-.19-1.749-1.892-.499-.872-.876-1.834-.876-1.834s-.072-.18-.203-.276c-.158-.117-.379-.155-.379-.155l-2.349.015s-.353.01-.482.166c-.115.139-.009.428-.009.428s1.84 4.337 3.927 6.524c1.914 2.005 4.087 1.875 4.087 1.875h.984z" /></svg>
          </a>
        </li>
        <li>
          <a href="https://www.instagram.com/new_gen_school/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
          </a>
        </li>
        <li>
          <a href="https://max.ru/join/oy92iwI3AHcutHF0oUf05Jllfl9sRkfmr9Lwdrf_Pq4" target="_blank" rel="noopener noreferrer" aria-label="MAX">
            {/* Тот же исходный path, но c одинаковым viewBox 24×24 — ужимаем
                родной 800×800 через preserveAspectRatio. */}
            <svg viewBox="0 0 800 800" fill="currentColor" preserveAspectRatio="xMidYMid meet"><path d="M350.4,9.6C141.8,20.5,4.1,184.1,12.8,390.4c3.8,90.3,40.1,168,48.7,253.7,2.2,22.2-4.2,49.6,21.4,59.3,31.5,11.9,79.8-8.1,106.2-26.4,9-6.1,17.6-13.2,24.2-22,27.3,18.1,53.2,35.6,85.7,43.4,143.1,34.3,299.9-44.2,369.6-170.3C799.6,291.2,622.5-4.6,350.4,9.6h0ZM269.4,504c-11.3,8.8-22.2,20.8-34.7,27.7-18.1,9.7-23.7-.4-30.5-16.4-21.4-50.9-24-137.6-11.5-190.9,16.8-72.5,72.9-136.3,150-143.1,78-6.9,150.4,32.7,183.1,104.2,72.4,159.1-112.9,316.2-256.4,218.6h0Z" /></svg>
          </a>
        </li>
        <li>
          <a href="https://t.me/newgeneration_school" target="_blank" rel="noopener noreferrer" aria-label="Telegram">
            {/* Bootstrap-icons bi-telegram, viewBox 16×16 — единый стиль с остальными. */}
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M8.287 5.906c-.778.324-2.334.994-4.666 2.01-.378.15-.577.298-.595.442-.03.243.275.339.69.47l.175.055c.408.133.958.288 1.243.294.26.006.549-.1.868-.32 2.179-1.471 3.304-2.214 3.374-2.23.05-.012.12-.026.166.016s.042.12.037.141c-.03.129-1.227 1.241-1.846 1.817-.193.18-.33.307-.358.336q-.094.095-.189.187c-.38.366-.664.64.015 1.088.327.216.589.393.85.571.284.194.568.387.936.629q.14.092.27.187c.331.236.63.448.997.414.214-.02.435-.221.547-.82.265-1.417.786-4.486.906-5.751a1.4 1.4 0 0 0-.013-.315.34.34 0 0 0-.114-.217.5.5 0 0 0-.31-.093c-.3.005-.763.166-2.984 1.09" /></svg>
          </a>
        </li>
      </ul>
    </footer>
  );
}

function ForgotPasswordModal({ onClose }) {
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setErr('Введите корректный email');
      return;
    }
    setErr(null);
    setSending(true);
    try {
      // Бэк всегда возвращает 200 OK, даже если email не зарегистрирован
      // (anti-enumeration). Нам тут достаточно убедиться, что запрос
      // прошёл — и показать одинаковое сообщение «проверьте почту».
      await api.post('/api/auth/password-reset/request', { email: v });
      setSent(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Не удалось отправить письмо. Попробуйте позже.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="lf-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lf-modal" role="dialog" aria-modal="true">
        <div className="lf-header">
          <div>
            <h2 className="lf-header__title">{sent ? 'Письмо отправлено' : 'Восстановление пароля'}</h2>
            {!sent && (
              <p className="lf-header__sub">
                Введите email, привязанный к учётной записи. Мы пришлём ссылку для создания нового пароля.
              </p>
            )}
          </div>
          <button className="lf-close" onClick={onClose} aria-label="Закрыть">
            <Icon name="x" size={16} />
          </button>
        </div>

        {sent ? (
          <>
            <div className="lf-body">
              <div className="lf-success">
                <div className="lf-success__icon"><Icon name="check" size={22} /></div>
                <p className="lf-success__title">Проверьте почту</p>
                <p className="lf-success__text">
                  Если адрес <span className="lf-success__email">{email}</span> привязан к учётной
                  записи, на него отправлено письмо со ссылкой для смены пароля. Ссылка действительна 2 часа.
                </p>
                <p className="lf-success__text" style={{ marginTop: 4 }}>
                  Не пришло письмо? Проверьте папку «Спам» или обратитесь к администратору школы.
                </p>
              </div>
            </div>
            <div className="lf-footer">
              <button className="lf-btn lf-btn--primary" onClick={onClose}>Готово</button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className="lf-body">
              <div className="login-field">
                <label className="login-field__label" htmlFor="lf-email">Email</label>
                <input
                  id="lf-email"
                  className={`login-field__input ${err ? 'login-field__input--error' : ''}`}
                  type="email"
                  autoFocus
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (err) setErr(null); }}
                  placeholder="you@np-school.ru"
                />
                {err && <div className="login-field__error">{err}</div>}
              </div>
            </div>
            <div className="lf-footer">
              <button type="button" className="lf-btn" onClick={onClose}>Отмена</button>
              <button type="submit" className="lf-btn lf-btn--primary" disabled={sending}>
                {sending ? 'Отправляем…' : 'Отправить ссылку'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
